import { useEffect, useState, useRef, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { RefreshCcw, Target, FileText, DollarSign, TrendingUp, Users, Activity, Database } from "lucide-react";
import api from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import AgentCard from "@/components/AgentCard";
import StatusPill from "@/components/StatusPill";
import PayloadTester from "@/components/PayloadTester";
import { useAuth } from "@/lib/auth";

const REFRESH_MS = 30000;

function PayloadTesterCollapsed() {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid="payload-tester-collapsed">
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-[#f5f6f8] border border-[#e7e9ee] rounded-xl text-sm font-semibold text-[#5c6877] hover:bg-[#eef1f6] transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid="payload-tester-toggle"
      >
        <span>Payload Tester</span>
        <span className="text-xs font-mono">{open ? "▲ collapse" : "▼ expand"}</span>
      </button>
      {open && (
        <div className="mt-2" data-testid="payload-tester-content">
          <PayloadTester />
        </div>
      )}
    </section>
  );
}

const BUCKET_LABEL = {
  HARD_CANCEL: "Hard cancel",
  UNCLASSIFIED: "Unclassified",
  EXCLUDE: "Excluded",
  STRUCTURAL_HOLD: "Structural hold",
  MISCLASSIFIED_RESCHEDULE: "Misclassified reschedule",
};
const BUCKET_TONE = {
  HARD_CANCEL: "bg-[#fdecec] text-[#c73535] border-[#f4c9c9]",
  UNCLASSIFIED: "bg-[#fff6dc] text-[#8a6a1c] border-[#f0dfa5]",
  EXCLUDE: "bg-[#eef1f6] text-[#485563] border-[#dbe0e8]",
  STRUCTURAL_HOLD: "bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]",
  MISCLASSIFIED_RESCHEDULE: "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]",
};

// Real-time HH:MM:SS clock + long date. Uses the client wall clock (accurate enough for
// dashboards); server_time from /overview is used only as a "last synced" anchor.
function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);
  return now;
}

function fmt$(n) {
  if (n == null) return "—";
  if (n >= 1000) return `$${Math.round(n).toLocaleString()}`;
  return `$${Number(n).toFixed(0)}`;
}

export default function Overview() {
  const { tenant } = useOutletContext();
  const { user } = useAuth();
  const [ov, setOv] = useState(null);
  const [techs, setTechs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [health, setHealth] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [countdown, setCountdown] = useState(REFRESH_MS / 1000);
  const hasLoadedOnceRef = useRef(false);
  const now = useClock();
  const isPlatformAdmin = user?.role === "platform_admin";

  const load = useCallback(async () => {
    try {
      const calls = [
        api.get("/overview"),
        api.get("/kpis/techs"),
        api.get("/agents"),
        api.get("/cancellations/buckets"),
      ];
      if (isPlatformAdmin) calls.push(api.get("/ingest/health"));
      const results = await Promise.all(calls);
      setOv(results[0].data);
      setTechs(results[1].data);
      setAgents(results[2].data.filter((x) => x.id !== "raymond"));
      setBuckets(results[3].data);
      if (isPlatformAdmin) setHealth(results[4].data);
      setLastSyncedAt(new Date());
      hasLoadedOnceRef.current = true;
    } catch {
      // Keep the last-good data mounted; only surface first-load failure via missing `ov`
    }
    setCountdown(REFRESH_MS / 1000);
  }, [isPlatformAdmin]);

  // Initial + polling every 30s
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const iv = setInterval(load, REFRESH_MS);
    return () => clearInterval(iv);
  }, [load]);

  // Countdown ticker for "Refreshes in Ns"
  useEffect(() => {
    const iv = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(iv);
  }, []);

  // Overview clock + "Last synced" tooltip + Ingest Health "Last checked"
  // all use 12-hour AM/PM format per Brett's 2026-07-29 request — the
  // 24h "16:32:20" style was disorienting on a page that's otherwise
  // written in casual business voice.
  const clockStr = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true });
  const dateStr = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  const maxObp = techs.length ? Math.max(...techs.map((t) => t.obp_wtd)) : 1;
  const tag = ov?.techs_at_goal;
  const pctAtGoal = tag ? Math.min(100, Math.round((tag.at_goal / Math.max(tag.total, 1)) * 100)) : 0;

  // Agents Online tile uses the same filtered list as the Roster (raymond excluded)
  const agentsOnline = agents.filter((a) => ["active", "idle"].includes(a.status)).length;
  const agentsTotal = agents.length;
  const agentsAlerted = agents.filter((a) => a.status === "alert").map((a) => a.name);

  return (
    <div className="space-y-6" data-testid="overview-page">
      {/* Header — big clock + date + sync indicator */}
      <header className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[.15em] text-[color:var(--cc-ink-muted)] font-semibold">
            {tenant?.location}
          </div>
          <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1">
            {tenant?.business_name}
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1">
            Command hub — real-time snapshot across all operating units.
          </p>
        </div>
        <div className="text-center md:border-x md:border-[#eef1f6] md:px-6">
          <div
            className="font-display font-bold text-[32px] sm:text-[40px] tracking-tight text-[#0e1420] tabular-nums"
            data-testid="overview-clock"
          >
            {clockStr}
          </div>
          <div className="text-[12px] text-[color:var(--cc-ink-muted)] font-mono-data" data-testid="overview-date">
            {dateStr}
          </div>
        </div>
        <div className="flex justify-end min-w-0">
          <div
            data-testid="overview-sync-indicator"
            className="inline-flex items-center gap-2 text-[11.5px] font-mono-data uppercase tracking-widest bg-[#e7f4fb] text-[#1c6a99] border border-[#c5e0ee] rounded-full px-3 py-1.5 font-bold"
            title={lastSyncedAt ? `Last synced ${lastSyncedAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", hour12: true })}` : ""}
          >
            <RefreshCcw size={12} strokeWidth={2.2} className={countdown === 0 ? "animate-spin" : ""} />
            {ov ? `Refreshes in ${countdown}s` : "Loading…"}
          </div>
        </div>
      </header>

      {/* Ingest Health strip — platform_admin only */}
      {isPlatformAdmin && health && (
        <section
          data-testid="ingest-health-strip"
          className="bg-white border border-[#e7e9ee] rounded-xl p-3 sm:p-4"
        >
          <div className="flex items-center gap-2 mb-2.5">
            <Database size={13} strokeWidth={2} className="text-[#0072bc]" />
            <div className="text-[11px] uppercase tracking-[.09em] text-[#0e1420] font-bold">Ingest Health</div>
            <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
            · Platform admin · Last checked{" "}{new Date(health.checked_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true })}
            </div>
            <div className="ml-auto text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)] hidden sm:block" data-testid="ingest-health-legend">
              green &lt;⅓ cadence · yellow ⅓–1× · red ≥ cadence · never = no data
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {health.items.map((it) => {
              const tone = {
                green: "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]",
                yellow: "bg-[#fff6dc] text-[#8a6a1c] border-[#f0dfa5]",
                red: "bg-[#fdecec] text-[#c73535] border-[#f4c9c9]",
                never: "bg-[#f5f6f8] text-[#6b7280] border-[#e0e3ea]",
              }[it.status];
              const dotColor = {
                green: "#0e8a68", yellow: "#c8a12b", red: "#c73535", never: "#9ca3af",
              }[it.status];
              const hoursLabel = it.hours_since == null
                ? "never"
                : it.hours_since < 1 ? `${Math.round(it.hours_since * 60)}m ago`
                : it.hours_since < 24 ? `${Math.round(it.hours_since)}h ago`
                : `${Math.round(it.hours_since / 24)}d ago`;
              return (
                <div
                  key={it.collection}
                  data-testid={`health-cell-${it.collection}`}
                  className={`border rounded-lg px-2.5 py-2 ${tone}`}
                  title={
                    (it.last_at ? `Last ${it.freshness_field || "activity"} at ${it.last_at}` : "No timestamped rows found")
                    + (it.threshold_hours
                        ? ` · Threshold: ${it.threshold_hours}h (${{ per_feed: "per-feed via expected_cadence_hours", collection_default: "collection default" }[it.threshold_source] || "fallback default"})`
                        : "")
                  }
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ background: dotColor }}
                      data-testid={`health-dot-${it.collection}`}
                    />
                    <div className="text-[11.5px] font-mono-data font-bold text-[#0e1420] truncate flex-1">
                      {it.collection}
                    </div>
                  </div>
                  <div className="mt-0.5 flex items-baseline gap-1.5">
                    <span className="text-[11px] font-mono-data font-semibold uppercase tracking-widest">
                      {hoursLabel}
                    </span>
                    <span
                      className="text-[9.5px] font-mono-data ml-auto"
                      style={{ opacity: 0.65 }}
                      data-testid={`health-threshold-${it.collection}`}
                    >
                      {it.threshold_hours ? `${it.threshold_hours}h${{ per_feed: "", collection_default: " default" }[it.threshold_source] ?? "*"}` : ""}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-baseline gap-1.5">
                    <span className="text-[10px] font-mono-data text-[color:var(--cc-ink-muted)]">
                      {it.rows_last_24h} / {it.total_rows}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Payload Tester — platform_admin only. Moved to bottom (collapsed). */}

      {/* Primary KPI tiles (light theme) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Weekly Revenue"
          value={ov?.weekly_revenue != null ? fmt$(ov.weekly_revenue) : "—"}
          delta={ov?.weekly_revenue_week_of ? `Week of ${new Date(ov.weekly_revenue_week_of).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · completed jobs` : "No completed week yet"}
          tone="up"
          icon={DollarSign}
          testId="kpi-weekly-revenue"
        />
        <KpiCard
          label="Open Estimates"
          value={ov?.open_estimates?.count != null ? String(ov.open_estimates.count) : "—"}
          delta={
            ov?.open_estimates?.pipeline_usd != null
              ? `Pipeline ${fmt$(ov.open_estimates.pipeline_usd)}`
              : "Pipeline $ unavailable"
          }
          tone={ov?.open_estimates?.count ? "up" : "flat"}
          icon={FileText}
          testId="kpi-open-estimates"
        />
        <KpiCard
          label="Cancellation Rate"
          value={ov ? `${ov.cancel_rate_pct}%` : "—"}
          delta={ov ? `${ov.cancel_total} classified · target < 20%` : ""}
          tone={ov && ov.cancel_rate_pct > 22 ? "down" : "up"}
          icon={Activity}
          testId="kpi-cancel-rate"
        />
        <div
          data-testid="kpi-techs-at-goal"
          className="bg-white border border-[#e7e9ee] rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
        >
          <div className="flex items-center gap-1.5 mb-2">
            <Target size={12} strokeWidth={2.2} className="text-[#0072bc]" />
            <div className="text-[11px] uppercase tracking-[.09em] text-[#5c6877] font-semibold">Techs at Goal</div>
          </div>
          <div className="font-mono-data text-[26px] font-bold text-[#0e1420] leading-none tabular-nums">
            {tag ? `${tag.at_goal} / ${tag.total}` : "—"}
          </div>
          <div className="mt-2.5">
            <div className="h-[8px] rounded-full bg-[#eef1f6] overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${pctAtGoal}%`,
                  background: pctAtGoal >= 60 ? "#0e8a68" : pctAtGoal >= 30 ? "#FFC72C" : "#ef5757",
                }}
              />
            </div>
            <div className="text-[11px] font-mono-data mt-1.5 text-[color:var(--cc-ink-muted)]">
              {tag ? `${pctAtGoal}% at goal` : "—"}
              {tag?.have_goal !== undefined && tag.total > tag.have_goal ? ` · ${tag.total - tag.have_goal} no goal set` : ""}
            </div>
          </div>
        </div>
      </div>

      {/* Secondary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard
          label="Team OBP (WTD)"
          value={ov ? `$${ov.team_obp_wtd.toLocaleString()}` : "—"}
          delta={ov ? `${ov.above_floor} of ${ov.tech_count} techs above $2,500 floor` : ""}
          tone={ov && ov.above_floor >= ov.tech_count / 2 ? "up" : "down"}
          icon={TrendingUp}
          testId="kpi-team-obp"
        />
        <KpiCard
          label="TTM Revenue"
          value={ov ? `$${ov.ttm_revenue_millions}M` : "—"}
          delta="Trailing 12 months · all units"
          tone="up"
          icon={DollarSign}
          testId="kpi-ttm-revenue"
        />
        <KpiCard
          label="Agents Online"
          value={agentsTotal > 0 ? `${agentsOnline} / ${agentsTotal}` : "—"}
          delta={agentsAlerted.length ? `${agentsAlerted.join(", ")} flagged` : "All clear"}
          tone={agentsAlerted.length ? "down" : "up"}
          icon={Users}
          testId="kpi-agents-online"
        />
      </div>

      {/* Agent Roster */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-display font-semibold text-[18px] text-[#0e1420]">Agent Roster</h2>
          <span className="text-[11px] font-mono-data uppercase tracking-widest text-[color:var(--cc-ink-muted)]">
            {agents.length} agents · tenant-scoped
          </span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5" data-testid="overview-agent-grid">
          {agents.map((a) => (
            <AgentCard key={a.id} agent={a} />
          ))}
        </div>
      </section>

      {/* Two column */}
      <section className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 bg-white border border-[#e7e9ee] rounded-xl p-5" data-testid="obp-by-tech-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-[15px] text-[#0e1420]">OBP by Tech — WTD</h3>
            <StatusPill status="active" label="Live" />
          </div>
          <div className="space-y-2.5">
            {techs.map((t) => {
              const pct = Math.max(6, (t.obp_wtd / maxObp) * 100);
              const belowFloor = t.obp_wtd < 2500;
              return (
                <div key={t.id} className="grid grid-cols-[100px_1fr_auto] sm:grid-cols-[140px_1fr_100px] items-center gap-3">
                  <div className="text-[12.5px] text-[#0e1420] truncate">{t.tech_name}</div>
                  <div className="h-[10px] rounded-full bg-[#eef1f6] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${pct}%`,
                        background: belowFloor ? "#ef5757" : "#0072bc",
                      }}
                    />
                  </div>
                  <div className="text-[12.5px] font-mono-data font-semibold text-right text-[#0e1420]">
                    ${t.obp_wtd.toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-2 bg-white border border-[#e7e9ee] rounded-xl p-5" data-testid="cancel-buckets-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold text-[15px] text-[#0e1420]">Cancellations</h3>
            <span className="text-[11px] text-[color:var(--cc-ink-muted)] font-mono-data">
              {buckets.reduce((a, b) => a + b.count, 0)} classified
            </span>
          </div>
          <div className="space-y-2">
            {buckets.map((b) => (
              <div
                key={b.bucket}
                className="flex items-center justify-between py-2 border-b border-[#eef1f6] last:border-b-0"
                data-testid={`cancel-bucket-${b.bucket}`}
              >
                <span className="text-[13px] text-[#0e1420]">{BUCKET_LABEL[b.bucket]}</span>
                <span
                  className={`text-[11px] font-mono-data font-semibold px-2 py-1 rounded-md border ${
                    BUCKET_TONE[b.bucket]
                  }`}
                >
                  {b.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payload Tester — platform_admin only, collapsed by default */}
      {isPlatformAdmin && <PayloadTesterCollapsed />}
    </div>
  );
}
